/* eslint-disable react/no-array-index-key */
import React, { useRef, useId } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import NavMenu from '@concur/nui-widgets/NavMenu';
import Separator from '@concur/nui-widgets/Separator';
import useAutoOverflow from '@concur/nui-widgets/lib/utils/useAutoOverflow';
import { withFormatter } from '@concur/nui-intl-runtime';
import { compose } from '@concur/core-ui-shell';
import SideNavItem from './_SideNavItem';
import SideNavItemMenu from './_SideNavItemMenu';
import convertJavascriptUrl from '../utils/convertJavascriptUrl';
import styles from './SideNav.css';
import { getSidenavItemAnalyticsJson } from '../utils/getSidenavItemAnalyticsJson';
import { triggerSidenavItemAnalyticsClick } from '../utils/triggerSidenavItemAnalyticsClick';

const CSS_BLOCK = 'sapcnqr-sidenav';
const CSS_PREFIX = 'sapcnqr';
const FLYOUT_MAX_HEIGHT = 'calc(100vh - 16px)';

// SMNUI-28017: keep collapsed flyouts anchored to their trigger and on the side.
// Positioning is left entirely to Floating UI while fractional-zoom behavior is
// isolated; viewport-height limiting is applied as a static content cap.

const SideNav = (props) => {
    const {
        className,
        menuItems,
        formatter,
        activePathArray,
    } = props;

    const {
        homeMenuItem,
        applicationMenuItems,
        adminMenuItem,
        adminMenuItems,
        linksMenuItem,
        testDriveMenuItem,
        subscribeMenuItem,
    } = menuItems;

    const classnames = classnamesBind.bind(styles);

    const targetRef = useRef(null);

    const submenuDesc = formatter.formattedMessage({ id: 'CoreUI.sideNavSubmenuDescription' });

    // SMNUI-28017: NavMenu.Item (nested/overflow fly-outs) has no widget-managed
    // inner dialog like NavMenu, so give its popper content overflow:hidden and its
    // inner dialog overflow-y:auto here.
    const flyoutScrollOverlayProps = {
        // Horizontal placements only: nested fly-outs cascade to the side (arrow on the
        // left/right edge) and must never flip to top/bottom against the column boundary.
        popperPlacement: ['right-start', 'right-end', 'right', 'left-start', 'left-end', 'left'],
        popperContentProps: {
            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__flyout-overlay`),
            style: { maxHeight: FLYOUT_MAX_HEIGHT },
        },
        innerDialogClassName: classnames(CSS_PREFIX, `${CSS_BLOCK}__flyout-dialog`),
    };

    const classes = classnames(
        className,
        CSS_PREFIX,
        CSS_BLOCK,
    );

    const listClasses = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__list`,
    );

    const getListItemClasses = (item, hasSubItems) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__list-item`,
        {
            [`${CSS_BLOCK}__list-item--menu`]: !!hasSubItems,
            [`${CSS_BLOCK}__list-item--active`]: item?.isActive || activePathArray?.includes(item.id),
        },
    );

    const itemTextClasses = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__item-text`,
    );

    const subMenuItemText = classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__sub-item-text`,
    );

    const getItemClasses = (hasIcon) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__item`,
        {
            [`${CSS_BLOCK}__item--icon`]: !!hasIcon,
        },
    );

    const getSubItemClasses = (item) => classnames(
        CSS_PREFIX,
        `${CSS_BLOCK}__subitem`,
        {
            [`${CSS_BLOCK}__subitem--active`]: item?.isActive || activePathArray?.includes(item.id),
        },
    );

    const renderSubMenuItems = (items, level) => (
        items.map((subItem, index) => {
            if (subItem.subItems) {
                const buttonId = useId();
                const hasActiveSubItem = activePathArray?.includes(subItem.id);
                const adjustedSubItem = {
                    ...subItem,
                    isActive: subItem.isActive || hasActiveSubItem,
                };

                return (
                    <NavMenu.Item
                        label={subItem.name}
                        buttonProps={{
                            className: getSubItemClasses(adjustedSubItem),
                            innerTextClasses: subMenuItemText,
                            id: buttonId,
                            'data-test': `sidenav__subitem__button-${subItem.id}`,
                            innerProps: {
                                className: classnames(CSS_PREFIX, `${CSS_BLOCK}__subbutton`, `${CSS_BLOCK}__subbutton--${level}`),
                            },
                            'data-analytics-json': getSidenavItemAnalyticsJson(subItem.id),
                        }}
                        submenuListProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__container`),
                        }}
                        overlayProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__overlay`),
                            ...flyoutScrollOverlayProps,
                        }}
                        ariaCurrent={subItem.isActive}
                        linkProps={{
                            className: getSubItemClasses(adjustedSubItem),
                            innerTextClasses: subMenuItemText,
                            'data-test': `sidenav__subitem__link-${subItem.id}`,
                        }}
                        iconProps={{
                            iconName: 'navigation-right-arrow',
                        }}
                        key={`navitem_${level}_${index}`}
                        tooltipMessage={formatter.formattedMessage({ id: 'CoreUI.openSubMenu', values: { applicationName: subItem.name } })}
                        tooltipProps={{
                            outerPopperProps: {
                                showArrow: false,
                                offset: 3,
                            },
                            innerContainerProps: {
                                className: classnames(`${CSS_BLOCK}__tooltip-inner`),
                            },
                            delay: 1000,
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter'
                                 || e.key === ' '
                                  || e.key === 'ArrowRight') {
                                triggerSidenavItemAnalyticsClick(subItem.id);
                            }
                        }}
                    >
                        {renderSubMenuItems(subItem.subItems, level + 1)}
                    </NavMenu.Item>
                );
            }
            if (subItem.url) {
                return (
                    <NavMenu.Item
                        {...convertJavascriptUrl(subItem.url, subItem.id)}
                        label={subItem.name}
                        ariaCurrent={subItem.isActive ? 'page' : false}
                        linkProps={{
                            target: subItem?.target || 'self',
                            className: getSubItemClasses(subItem),
                            innerTextClasses: subMenuItemText,
                            'data-test': `sidenav__subitem__link-${subItem.id}`,
                            'data-analytics-json': getSidenavItemAnalyticsJson(subItem.id),
                        }}
                        key={`navitem_${level}_${index}`}
                    />
                );
            }
            return null;
        }));

    const getNavChildren = (item, index) => {
        if (item?.subItems) {
            const buttonId = useId();
            const hasActiveSubItem = activePathArray?.includes(item.id);
            const adjustedItem = {
                ...item,
                isActive: item.isActive || hasActiveSubItem,
            };

            // only add active class  to duplicate group header link entry
            // if there are no active subitems
            // otherwise there will be 2 list items highlighted
            const isActive = item?.isActive && !hasActiveSubItem;

            // menuData from Menu.cshtml will have urls of "#" sometimes in place of being empty
            // we do not want these to be considered urls for NavMenu.Group headers
            const hasValidUrl = item.url && item.url !== '#';

            return (
                <SideNavItemMenu
                    item={adjustedItem}
                    data-test={`sidenav__listitem-${item.id}`}
                    iconProps={{
                        iconName: 'navigation-right-arrow',
                        className: classnames(CSS_PREFIX, `${CSS_BLOCK}__icon`),
                        'data-test': `sidenav__icon-${item.id}`,
                    }}
                    buttonProps={{
                        innerProps: {
                            className: getItemClasses(true),
                        },
                        className: getListItemClasses(adjustedItem, true),
                        innerTextClasses: itemTextClasses,
                        id: buttonId,
                        'aria-label': `${adjustedItem.name}, ${submenuDesc}`,
                        'data-test': `sidenav__button-${item.id}`,
                        'data-analytics-json': getSidenavItemAnalyticsJson(item.id),
                    }}
                    className={classnames(CSS_PREFIX, `${CSS_BLOCK}__navmenu`)}
                    key={`navitem_1_${index}`}
                    menuAriaLabel={item.name}
                    overlayProps={{
                        showArrow: true,
                        offset: 5,
                        popperPlacement: ['right-start', 'right-end', 'right'],
                        popperContentProps: {
                            style: { maxHeight: FLYOUT_MAX_HEIGHT },
                        },
                        'data-sidenav-popover': true,
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter'
                            || e.key === ' '
                            || e.key === 'ArrowRight') {
                            triggerSidenavItemAnalyticsClick(item.id);
                        }
                    }}
                >
                    {/* Only using NavMenu.Group headers when an
                    item with subitems does not have a valid url */}
                    <NavMenu.Group
                        header={!hasValidUrl ? item.name : undefined}
                        headerProps={{
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__header`),
                        }}
                    >
                        {/* duplicate item for group header when valid url is provided */}
                        {hasValidUrl && (
                            <NavMenu.Item
                                label={item.name}
                                ariaCurrent={isActive ? 'page' : false}
                                linkProps={{
                                    className: classnames(
                                        CSS_PREFIX,
                                        `${CSS_BLOCK}__subitem`,
                                        `${CSS_BLOCK}__subitem--header`,
                                        {
                                            [`${CSS_BLOCK}__subitem--active`]: isActive,
                                        },
                                    ),
                                    innerTextClasses: subMenuItemText,
                                    'data-test': `sidenav__subitem__link-${item.id}`,
                                }}
                                href={item.url}
                                data-analytics-json={getSidenavItemAnalyticsJson(item.id)}
                            />
                        )}
                        {renderSubMenuItems(item.subItems, 2)}
                    </NavMenu.Group>
                </SideNavItemMenu>
            );
        }

        let additionalProps = {};
        if (index === 'home') {
            // Home is the top anchor — never overflow it into "More".
            additionalProps = {
                'data-no-overflow': true,
            };
        }

        return (
            <SideNavItem
                data-test={`sidenav__listitem-${item.id}`}
                {...additionalProps}
                className={getListItemClasses(item)}
                key={`navitem_1_${index}`}
                ariaCurrent={item.isActive ? 'page' : false}
                linkProps={{
                    className: getItemClasses(false),
                    innerTextClasses: itemTextClasses,
                    'data-test': `sidenav__link-${item.id}`,
                }}
                href={item.url}
                item={item}
                formatter={formatter}
                data-analytics-json={getSidenavItemAnalyticsJson(item.id)}
            />
        );
    };

    // Phase 2 (SMNUI-20048): Prefer adminMenuItems array (top-level items)
    // over adminMenuItem (collapsible parent). Falls back when absent or empty.
    const hasAdminMenuItems = Array.isArray(adminMenuItems)
        && adminMenuItems.length > 0;

    let adminChildren = [];
    const appChildren = applicationMenuItems
        ? applicationMenuItems.map((item, i) => getNavChildren(item, i))
        : [];

    if (hasAdminMenuItems) {
        // Phase 2: render adminMenuItems in their natural source order.
        adminChildren = adminMenuItems.map(
            (item, i) => getNavChildren(item, `admin${i}`),
        );
    } else if (adminMenuItem && adminMenuItem.subItems) {
        // Phase 1: single Administration entry.
        adminChildren = [getNavChildren(adminMenuItem, 'Administration')];
    }

    const renderOverflowChild = (child) => {
        if (child == null) return null;
        if (child.type?.displayName === 'Separator') return null;

        const item = child.props.item;

        if (item?.subItems) {
            const hasValidUrl = item.url && item.url !== '#';
            return (
                <NavMenu.Item
                    label={item.name}
                    key={`overflow_${item.id}`}
                    buttonProps={{
                        className: getSubItemClasses(item),
                        innerTextClasses: subMenuItemText,
                        // __subbutton inner class carries the sidenav text color;
                        // without it the Button text falls back to link-blue.
                        innerProps: {
                            className: classnames(CSS_PREFIX, `${CSS_BLOCK}__subbutton`, `${CSS_BLOCK}__subbutton--2`),
                        },
                        'aria-label': `${item.name}, ${submenuDesc}`,
                        'data-test': `sidenav__button-${item.id}`,
                        'data-analytics-json': getSidenavItemAnalyticsJson(item.id),
                    }}
                    submenuListProps={{ className: classnames(CSS_PREFIX, `${CSS_BLOCK}__container`) }}
                    overlayProps={{
                        className: classnames(CSS_PREFIX, `${CSS_BLOCK}__overlay`),
                        ...flyoutScrollOverlayProps,
                        // Overflow submenus are siblings in one overlay. The framework's
                        // single-open coordination (handleOutsideClick) reads data-level only
                        // from the click target and its immediate parent — but the menu label
                        // renders in a nested span with no data-level, so clicking a sibling's
                        // TEXT misreads the level and leaves the previously-open submenu open.
                        // Delegate with a closest()-based level read: a same-level (0) sibling
                        // click closes this submenu; a deeper descendant fly-out click keeps it.
                        onClickOutside: (event) => {
                            const { target } = event;
                            const leafClicked = !!target?.closest?.('[data-nui-widgets="menuitem-leaf"]');
                            const nodeClicked = !!target?.closest?.('[data-nui-widgets="menuitem-node"]');
                            const levelEl = target?.closest?.('[data-level]');
                            const isLevelDiff = levelEl?.getAttribute('data-level') !== '0';
                            return !!(leafClicked || (nodeClicked && isLevelDiff));
                        },
                    }}
                    iconProps={{ iconName: 'navigation-right-arrow' }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter'
                            || e.key === ' '
                            || e.key === 'ArrowRight') {
                            triggerSidenavItemAnalyticsClick(item.id);
                        }
                    }}
                >
                    <NavMenu.Group
                        header={!hasValidUrl ? item.name : undefined}
                        headerProps={{ className: classnames(CSS_PREFIX, `${CSS_BLOCK}__header`) }}
                    >
                        {hasValidUrl && (
                            <NavMenu.Item
                                {...convertJavascriptUrl(item.url, item.id)}
                                label={item.name}
                                linkProps={{
                                    className: classnames(CSS_PREFIX, `${CSS_BLOCK}__subitem`, `${CSS_BLOCK}__subitem--header`),
                                    innerTextClasses: subMenuItemText,
                                    'data-test': `sidenav__subitem__link-${item.id}`,
                                }}
                            />
                        )}
                        {renderSubMenuItems(item.subItems, 2)}
                    </NavMenu.Group>
                </NavMenu.Item>
            );
        }

        return React.cloneElement(child, {
            inOverflowMenu: true,
            linkProps: {
                // preserve the leaf's own linkProps (data-test etc.); the overflow
                // row deliberately overrides className/innerTextClasses for submenu look
                ...child.props.linkProps,
                className: getSubItemClasses(item),
                innerTextClasses: subMenuItemText,
            },
        });
    };

    // SMNUI-28015: visual separator between the last client (end-user) item and
    // the first administrator item. Only when an admin section exists AND at
    // least one client item precedes it. Stripped from the overflow "More" menu
    // (see renderOverflowChild) so it never appears as a stray line in the popover.
    const hasAdminSection = adminChildren.length > 0;
    const hasClientItems = !!homeMenuItem || !!testDriveMenuItem
        || !!subscribeMenuItem || appChildren.length > 0;
    const showClientAdminSeparator = hasAdminSection && hasClientItems;

    const { isOverflowing, childrenToRender, overflowChildrenToRender } = useAutoOverflow({
        targetRef,
        // for useAutoOverflow to work properly, children needs to be an array
        children:
            [
                homeMenuItem ? getNavChildren(homeMenuItem, 'home') : [],
                testDriveMenuItem ? getNavChildren(testDriveMenuItem, 'testDrive') : [],
                subscribeMenuItem ? getNavChildren(subscribeMenuItem, 'subscribe') : [],
                ...appChildren,
                showClientAdminSeparator ? (
                    <Separator className={classnames(CSS_PREFIX, `${CSS_BLOCK}__separator`)} />
                ) : [],
                ...adminChildren,
                linksMenuItem ? linksMenuItem.subItems && getNavChildren(linksMenuItem, 'Favorites') : [],
            ],
        overflowPadding: 44,
        orientation: 'vertical',
        observedDimension: 'height',
        // SMNUI-25373: was misnamed `debounce: 1000` (silently ignored — the
        // hook's prop is debounceDelay). Keep at the framework default of
        // 150ms; 1000ms felt laggy when actually applied.
        debounceDelay: 150,
    });

    const isOverflowActive = overflowChildrenToRender?.some(
        (child) => {
            const item = child?.props?.item;
            return item && (item.isActive || activePathArray?.includes(item.id));
        },
    ) || false;

    const overflowMenuItem = {
        name: formatter.formattedMessage({ id: 'CoreUIComponents.more' }),
        id: 'overflow',
        isActive: isOverflowActive,
    };

    return (
        <NavMenu.Bar
            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.sideNavAriaLabel' })}
            className={classes}
            listProps={{
                className: listClasses,
                ref: targetRef,
            }}
            orientation="vertical"
            id="sideNavBar"
            data-test="sidenavbar"
        >
            {childrenToRender}
            {isOverflowing && (
                <SideNavItemMenu
                    item={overflowMenuItem}
                    data-test={`sidenav__listitem-${overflowMenuItem.id}`}
                    iconProps={{
                        iconName: 'navigation-right-arrow',
                        className: classnames(CSS_PREFIX, `${CSS_BLOCK}__icon`),
                    }}
                    buttonProps={{
                        innerProps: {
                            className: getItemClasses(true),
                        },
                        className: getListItemClasses(overflowMenuItem, true),
                        innerTextClasses: itemTextClasses,
                        'aria-label': overflowMenuItem.name,
                    }}
                    className={classnames(CSS_PREFIX, `${CSS_BLOCK}__navmenu`)}
                    key="navitem_overflow"
                    menuAriaLabel={overflowMenuItem.name}
                    overlayProps={{
                        showArrow: true,
                        offset: 5,
                        popperPlacement: ['right-start', 'right-end', 'right'],
                        popperContentProps: {
                            style: { maxHeight: FLYOUT_MAX_HEIGHT },
                        },
                        'data-sidenav-popover': true,
                    }}
                >
                    {React.Children.map(overflowChildrenToRender, renderOverflowChild)}
                </SideNavItemMenu>
            )}
        </NavMenu.Bar>
    );
};

SideNav.displayName = 'SideNav';

SideNav.propTypes = {
    menuItems: PropTypes.object.isRequired,
    className: PropTypes.string,
    activePathArray: PropTypes.arrayOf(PropTypes.string),
};

export default compose(
    withFormatter,
)(SideNav);
